import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/en';
import { translations as esTranslations } from '../translations/es';

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage } = useLanguage();
  const t = language === 'en' ? translations : esTranslations;

  // Ref and state for logo width
  const logoRef = useRef(null);
  const [logoWidth, setLogoWidth] = useState(null);
  useEffect(() => {
    if (logoRef.current) {
      setLogoWidth(logoRef.current.offsetWidth);
    }
  }, []);

  return (
    <nav className="sticky top-0 z-50 h-14 md:h-24 bg-[#181c24]/90 backdrop-blur-md shadow-lg font-playfair text-lg font-light py-1 md:py-3">
      <div className="max-w-6xl mx-auto flex items-center justify-between px-6 md:px-12 h-full">
        {/* Logo/Brand */}
        <Link to="/" className="flex flex-col items-start no-underline hover:no-underline relative pl-0">
          <span
            className="font-playfair text-xl md:text-2xl font-extrabold tracking-[0.2em] uppercase text-[#bfa76a] leading-tight block relative"
            ref={logoRef}
            style={{ display: 'inline-block' }}
          >
            Assemblage
            <span
              className="block h-0.5 bg-[#bfa76a] rounded-full absolute left-0 right-0 mt-1"
              style={{ width: '100%', bottom: '-0.2em', position: 'absolute' }}
            ></span>
          </span>
          <span
            className="font-playfair text-sm md:text-base font-light tracking-[0.25em] text-[#bfa76a]/80 mt-0.5 block whitespace-nowrap mx-auto text-center hidden md:block"
            style={logoWidth ? { width: `${logoWidth}px` } : {}}
          >
            {language === 'en' ? 'Weddings & More' : 'Bodas & Mas'}
          </span>
        </Link>

        {/* Hamburger menu and language selector on mobile */}
        <div className="flex items-center md:hidden gap-2 ml-auto pr-2">
          <button
            onClick={toggleLanguage}
            className="text-white hover:text-[#bfa76a] transition-colors font-playfair border border-[#bfa76a] px-3 py-1 rounded"
            style={{ minWidth: 44 }}
          >
            {language === 'en' ? 'ES' : 'EN'}
          </button>
          <button
            className="text-white focus:outline-none"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Desktop Nav */}
        <ul className="hidden md:flex items-center font-semibold space-x-8">
          <li>
          <Link
            to="/destinations"
            className={`relative text-base md:text-lg font-medium transition px-2 py-1 tracking-wide text-white hover:text-[#bfa76a] ${
                location.pathname === '/destinations' ? 'after:absolute after:left-1/2 after:-translate-x-1/2 after:-bottom-1 after:w-6 after:h-0.5 after:bg-[#bfa76a] after:rounded-full after:content-[\" \" ]' : ''
            }`}
          >
            {t.nav.destinations}
          </Link>
          </li>

          <li>
            <Link
              to="/resources"
              className={`relative text-base md:text-lg font-medium transition px-2 py-1 tracking-wide text-white hover:text-[#bfa76a] ${
                location.pathname === '/resources' ? 'after:absolute after:left-1/2 after:-translate-x-1/2 after:-bottom-1 after:w-6 after:h-0.5 after:bg-[#bfa76a] after:rounded-full after:content-[\" \" ]' : ''
              }`}
            >
              {t.nav.resources || (language === 'en' ? 'Resources' : 'Recursos')}
            </Link>
          </li>
          <li>
          <Link
            to="/contact"
            className={`relative text-base md:text-lg font-medium transition px-2 py-1 tracking-wide text-white hover:text-[#bfa76a] ${
                location.pathname === '/contact' ? 'after:absolute after:left-1/2 after:-translate-x-1/2 after:-bottom-1 after:w-6 after:h-0.5 after:bg-[#bfa76a] after:rounded-full after:content-[\" \" ]' : ''
            }`}
          >
            {t.nav.contact}
          </Link>
          </li>
          <li>
          <button
            onClick={toggleLanguage}
            className="text-white hover:text-[#bfa76a] transition-colors font-playfair border border-[#bfa76a] px-3 py-1 rounded"
          >
            {language === 'en' ? 'ES' : 'EN'}
          </button>
          </li>
        </ul>
      </div>

      {/* Mobile Nav */}
      {open && (
        <div className="md:hidden bg-[#181c24]/95 px-6 pb-6 pt-4 flex flex-col gap-4 font-semibold rounded-b-2xl max-h-[80vh] overflow-y-auto">
          <Link
            to="/"
            className={`text-lg font-medium transition text-white hover:text-[#bfa76a] py-2 tracking-wide ${
              location.pathname === '/' ? 'border-b border-[#bfa76a]' : ''
            }`}
            onClick={() => setOpen(false)}
          >
            {t.nav.home}
          </Link>
          <Link
            to="/destinations"
            className={`text-lg font-medium transition text-white hover:text-[#bfa76a] py-2 tracking-wide ${
              location.pathname === '/destinations' ? 'border-b border-[#bfa76a]' : ''
            }`}
            onClick={() => setOpen(false)}
          >
            {t.nav.destinations}
          </Link>
          {/* Guide Menu - Only show on destination detail pages, indented under Destinations */}
          {location.pathname.startsWith('/destinations/') && location.pathname !== '/destinations/' && (
            <div className="pl-6 border-l-2 border-[#bfa76a]/20 space-y-1 mt-1">
              <a href="#why-destination" className="block text-base font-medium transition text-white/80 hover:text-[#bfa76a] py-1 tracking-wide" onClick={() => setOpen(false)}>
                {language === 'en' ? `Why ${location.pathname.split('/').pop().split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}` : `¿Por qué ${location.pathname.split('/').pop().split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}?`}
              </a>
              <a href="#venues" className="block text-base font-medium transition text-white/80 hover:text-[#bfa76a] py-1 tracking-wide" onClick={() => setOpen(false)}>
                {language === 'en' ? 'Dream Venues' : 'Lugares de Ensueño'}
              </a>
              <a href="#experiences" className="block text-base font-medium transition text-white/80 hover:text-[#bfa76a] py-1 tracking-wide" onClick={() => setOpen(false)}>
                {language === 'en' ? 'Experiences' : 'Experiencias'}
              </a>
              <a href="#accommodation" className="block text-base font-medium transition text-white/80 hover:text-[#bfa76a] py-1 tracking-wide" onClick={() => setOpen(false)}>
                {language === 'en' ? 'Accommodation' : 'Alojamiento'}
              </a>
              <a href="#dining" className="block text-base font-medium transition text-white/80 hover:text-[#bfa76a] py-1 tracking-wide" onClick={() => setOpen(false)}>
                {language === 'en' ? 'Dining' : 'Gastronomía'}
              </a>
            </div>
          )}
          <Link
            to="/resources"
            className={`text-lg font-medium transition text-white hover:text-[#bfa76a] py-2 tracking-wide ${
              location.pathname === '/resources' ? 'border-b border-[#bfa76a]' : ''
            }`}
            onClick={() => setOpen(false)}
          >
            {t.nav.resources || (language === 'en' ? 'Resources' : 'Recursos')}
          </Link>
          <Link
            to="/contact"
            className={`text-lg font-medium transition text-white hover:text-[#bfa76a] py-2 tracking-wide ${
              location.pathname === '/contact' ? 'border-b border-[#bfa76a]' : ''
            }`}
            onClick={() => setOpen(false)}
          >
            {t.nav.contact}
          </Link>
        </div>
      )}
    </nav>
  );
}

export default Navbar;